import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

// --- 🚀 GLOBAL COMPONENTS ---
import Navbar from './components/common/Navbar';
import TransitionLoader from './components/common/TransitionLoader';

// --- LAYOUTS ---
import PublicLayout from './layouts/PublicLayout';

// --- GUARDS ---
import ProtectedRoute from './components/ProtectedRoute';
import AdminRoute from './components/AdminRoute';
import InstructorRoute from './components/InstructorRoute';

// --- LAYOUT WRAPPERS ---
import AdminLayout from './features/admin/components/AdminLayout';
import InstructorLayout from './features/instructor/components/InstructorLayout';

// --- 🛠️ TEMPORARY PLACEHOLDER COMPONENT (For Future Pages) ---
const ComingSoon = ({ title }) => (
  <div className="min-h-[60vh] flex flex-col items-center justify-center text-center px-4">
    <div className="w-20 h-20 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center mb-6 text-3xl">
      🚧
    </div>
    <h1 className="text-3xl font-bold text-gray-900 mb-2">{title}</h1>
    <p className="text-gray-500 max-w-md">
      نحن نعمل بجد لبناء هذه الصفحة. ستكون متاحة قريباً مع مميزات رائعة!
    </p>
    <button onClick={() => window.history.back()} className="mt-6 text-indigo-600 hover:underline font-bold">
      العودة للصفحة السابقة
    </button>
  </div>
);

// --- FEATURE IMPORTS ---

// 1. Auth
const SignUp = lazy(() => import('./features/auth/components/SignUp'));
const Login = lazy(() => import('./features/auth/components/Login'));
const ForgotPassword = lazy(() => import('./features/auth/components/ForgotPassword'));
const CompleteProfile = lazy(() => import('./features/auth/components/CompleteProfile'));
const VerifyEmail = lazy(() => import('./features/auth/components/VerifyEmail'));
const ConfirmEmail = lazy(() => import('./features/auth/components/ConfirmEmail'));
const ResetPassword = lazy(() => import('./features/auth/components/ResetPassword'));
const AuthAction = lazy(() => import('./features/auth/components/AuthAction'));

// 2. Admin
const AdminDashboard = lazy(() => import('./features/admin/components/AdminDashboard'));
const AdminUsersList = lazy(() => import('./features/admin/components/AdminUsersList'));
const AdminUserDetail = lazy(() => import('./features/admin/components/AdminUserDetail'));
const AdminCoursesList = lazy(() => import('./features/admin/components/AdminCoursesList'));
const ManageLessons = lazy(() => import('./features/admin/components/ManageLessons'));
const AddCourse = lazy(() => import('./features/admin/components/AddCourse'));
const AddArticle = lazy(() => import('./features/admin/components/AddArticle'));
const AdminArticlesList = lazy(() => import('./features/admin/components/AdminArticlesList'));
const EditArticle = lazy(() => import('./features/admin/components/EditArticle'));
const EditCourse = lazy(() => import('./features/admin/components/EditCourse'));
const ManageQuiz = lazy(() => import('./features/admin/components/ManageQuiz'));
const AdminEnrollments = lazy(() => import('./features/admin/components/AdminEnrollments'));

// 3. Instructor
const InstructorDashboard = lazy(() => import('./components/instructor/components/InstructorDashboard'));
const InstructorCourses = lazy(() => import('./components/instructor/components/InstructorCourses'));

// 4. Public/Shared
const HomePage = lazy(() => import('./pages/HomePage'));
const InfoPage = lazy(() => import('./pages/InfoPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const AcademyPage = lazy(() => import('./pages/AcademyPage')); 
const ArticlesPage = lazy(() => import('./pages/ArticlesPage'));
const ArticlePage = lazy(() => import('./pages/ArticlePage'));
const CoursePlayer = lazy(() => import('./pages/CoursePlayer'));
const QuizPage = lazy(() => import('./pages/QuizPage'));
const CertificatePage = lazy(() => import('./pages/CertificatePage'));
const NotFound = lazy(() => import('./components/NotFound'));

// ✅ NEW: Import the new Courses Page
const Courses = lazy(() => import('./pages/Courses')); 
const CourseDetail = lazy(() => import('./components/CourseDetail'));

const Dashboard = lazy(() => import('./components/Dashboard'));
const ProfilePage = lazy(() => import('./components/ProfilePage'));


const AppRoutes = () => {
  return (
    <div className="min-h-screen flex flex-col font-sans text-right" dir="rtl">
      
      {/* 🚀 NAVBAR GLOBAL */}
      <Navbar />

      <Suspense fallback={<TransitionLoader />}>
        <Routes>
          
          {/* =========================================================
              🌍 PUBLIC ROUTES
          ========================================================= */}
          <Route element={<PublicLayout />}>
            
            {/* --- Core Pages --- */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/info" element={<InfoPage />} />
            
            {/* --- 🎓 ACADEMY & COURSES --- */}
            {/* <Route path="/academy" element={<AcademyPage />} /> */}
            
            {/* 🚀 UPDATE: Point /courses to the new Courses.js */}
            <Route path="/courses" element={<Courses />} /> 
            <Route path="/courses/all" element={<Courses />} />
            
            {/* Note: If departments/levels are just filters, point them to Courses too, 
                otherwise keep AcademyPage if they are distinct landing pages */}
            <Route path="/courses/departments" element={<Courses />} />
            <Route path="/courses/levels" element={<Courses />} />
            
            <Route path="/courses/:courseId" element={<CourseDetail />} />

            {/* --- 📚 LESSONS & MATERIALS (Placeholders) --- */}
            <Route path="/lessons/recorded" element={<ComingSoon title="الدروس المسجلة" />} />
            <Route path="/lessons/live" element={<ComingSoon title="البث المباشر" />} />
            <Route path="/lessons/crash-courses" element={<ComingSoon title="مراجعات الامتحانات" />} />
            <Route path="/materials/docs" element={<ComingSoon title="المواد الدراسية PDF" />} />
            <Route path="/materials/cheatsheets" element={<ComingSoon title="ملخصات Cheat Sheets" />} />
            <Route path="/materials/questions" element={<ComingSoon title="بنوك الأسئلة" />} />
            <Route path="/labs/virtual" element={<ComingSoon title="المعامل الافتراضية" />} />
            <Route path="/projects/real-world" element={<ComingSoon title="مشاريع تطبيقية" />} />
            <Route path="/projects/graduation" element={<ComingSoon title="دعم مشاريع التخرج" />} />

            {/* --- 🤝 COMMUNITY (Placeholders) --- */}
            <Route path="/community" element={<ComingSoon title="مجتمع iTalebTech" />} />
            <Route path="/community/discussions" element={<ComingSoon title="النقاشات التقنية" />} />
            <Route path="/community/qa" element={<ComingSoon title="سؤال وجواب" />} />
            <Route path="/community/groups/*" element={<ComingSoon title="المجموعات الدراسية" />} />
            <Route path="/challenges/*" element={<ComingSoon title="التحديات والمسابقات" />} />
            <Route path="/mentors/*" element={<ComingSoon title="الإرشاد والتوجيه" />} />

            {/* --- 🛒 STORE (Placeholders) --- */}
            <Route path="/store" element={<ComingSoon title="المتجر الإلكتروني" />} />
            <Route path="/store/*" element={<ComingSoon title="المتجر الإلكتروني" />} />

            {/* --- 💡 INSIGHTS & ARTICLES --- */}
            <Route path="/news" element={<ComingSoon title="أخبار التكنولوجيا" />} />
            <Route path="/news/*" element={<ComingSoon title="الأخبار" />} />
            <Route path="/articles" element={<ArticlesPage />} />
            <Route path="/articles/:articleId" element={<ArticlePage />} />
            <Route path="/media/*" element={<ComingSoon title="الميديا والبودكاست" />} />

            {/* --- 💼 CAREER (Placeholders) --- */}
            <Route path="/jobs" element={<ComingSoon title="مركز التوظيف" />} />
            <Route path="/jobs/*" element={<ComingSoon title="فرص العمل والتدريب" />} />
            <Route path="/roadmaps" element={<ComingSoon title="المسارات التعليمية" />} />
            <Route path="/roadmaps/*" element={<ComingSoon title="خارطة الطريق" />} />
            <Route path="/cv/*" element={<ComingSoon title="خدمات السيرة الذاتية" />} />

            {/* --- ℹ️ ABOUT & LEGAL --- */}
            <Route path="/about/mission" element={<AboutPage />} />
            <Route path="/about/partners" element={<ComingSoon title="شركاء النجاح" />} />
            <Route path="/instructors" element={<ComingSoon title="فريق المدربين" />} />
            <Route path="/contact" element={<ComingSoon title="اتصل بنا" />} />
            <Route path="/help" element={<ComingSoon title="مركز المساعدة" />} />
            <Route path="/privacy" element={<ComingSoon title="سياسة الخصوصية" />} />
            <Route path="/terms" element={<ComingSoon title="الشروط والأحكام" />} />
            <Route path="/cookies" element={<ComingSoon title="ملفات الارتباط" />} />

            {/* --- 🔐 AUTH PAGES --- */}
            <Route path="/signup" element={<SignUp />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
          </Route>

          {/* =========================================================
              ⚙️ TECHNICAL ROUTES
          ========================================================= */}
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/confirm-email" element={<ConfirmEmail />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/auth-action" element={<AuthAction />} />

          {/* =========================================================
              👤 STUDENT PROTECTED ROUTES
          ========================================================= */}
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
          <Route path="/complete-profile" element={<ProtectedRoute><CompleteProfile /></ProtectedRoute>} />
          <Route path="/certificate/:courseId" element={<ProtectedRoute><CertificatePage /></ProtectedRoute>} />
          
          <Route path="/orders" element={<ProtectedRoute><ComingSoon title="طلباتي" /></ProtectedRoute>} />
          <Route path="/wallet" element={<ProtectedRoute><ComingSoon title="المحفظة والنقاط" /></ProtectedRoute>} />
          <Route path="/certificates" element={<ProtectedRoute><ComingSoon title="شهاداتي" /></ProtectedRoute>} />
          <Route path="/settings" element={<ProtectedRoute><ComingSoon title="إعدادات الحساب" /></ProtectedRoute>} />

          {/* 🚀 LEARNING MODE */}
          <Route path="/course/:courseId/learn" element={<ProtectedRoute><CoursePlayer /></ProtectedRoute>} />
          <Route path="/course/:courseId/quiz" element={<ProtectedRoute><QuizPage /></ProtectedRoute>} />

          {/* =========================================================
              🔐 ADMIN ROUTES
          ========================================================= */}
          <Route path="/admin" element={
            <ProtectedRoute>
              <AdminRoute>
                <AdminLayout />
              </AdminRoute>
            </ProtectedRoute>
          }>
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="users" element={<AdminUsersList />} />
            <Route path="user/:userId" element={<AdminUserDetail />} />
            <Route path="courses" element={<AdminCoursesList />} />
            <Route path="add-course" element={<AddCourse />} />
            <Route path="edit-course/:courseId" element={<EditCourse />} />
            <Route path="course/:courseId/lessons" element={<ManageLessons />} />
            <Route path="course/:courseId/quiz" element={<ManageQuiz />} />
            <Route path="articles-list" element={<AdminArticlesList />} />
            <Route path="add-article" element={<AddArticle />} />
            <Route path="edit-article/:articleId" element={<EditArticle />} />
            <Route path="enrollments" element={<AdminEnrollments />} />
            <Route path="settings" element={<div>صفحة الإعدادات (قريباً)</div>} />

            <Route index element={<Navigate to="dashboard" replace />} />
          </Route>

          {/* =========================================================
              👨‍🏫 INSTRUCTOR ROUTES
          ========================================================= */}
          <Route path="/instructor" element={
            <ProtectedRoute>
              <InstructorRoute>
                <InstructorLayout />
              </InstructorRoute>
            </ProtectedRoute>
          }>
            <Route index element={<Navigate to="dashboard" replace />} />
            <Route path="dashboard" element={<InstructorDashboard />} />
            <Route path="courses" element={<InstructorCourses />} />
            <Route path="add-course" element={<AddCourse isInstructor={true} />} />
            <Route path="edit-course/:courseId" element={<EditCourse />} />
            <Route path="course/:courseId/lessons" element={<ManageLessons />} />
            <Route path="students" element={<ComingSoon title="الطلاب" />} />
            <Route path="wallet" element={<ComingSoon title="المحفظة والأرباح" />} />
            <Route path="settings" element={<ComingSoon title="إعدادات المدرب" />} />
          </Route>

          {/* =========================================================
              404 NOT FOUND
          ========================================================= */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </Suspense>
    </div>
  );
};

export default AppRoutes;